import {useEffect, useRef, useState} from "react";
import {BELL_INFO_STORAGE_KEY, BELL_INFO_UPDATED_EVENT, readBellInfo} from "../utils/BellStorageUtils";
import {BELL_SOUND_FILES} from "../constants/BellSounds";
import {getCachedBellObjectUrl, getRemoteBellUrl} from "../utils/BellSoundCache";
import {logBellPlay} from "../Services/BellLogService";

const LS_EVENT = "bell:localstorage";
const LAST_PLAYED_SLOT_STORAGE_KEY = "bell:last-played-slot";
const SCHEDULE_LOOKBACK_MS = 90 * 1000;
const PLAYED_SLOT_TTL_MS = 24 * 60 * 60 * 1000;
const NATIONAL_ANTHEM_SOUND = "/bells/national-anthem.mp3";
const DEFAULT_BELL_SOUND = "/bells/bell.mp3";

const normalizeSoundPath = (value = "") => {
    return String(value).trim().split("?")[0].split("#")[0].replace(/\\/g, "/").toLowerCase();
};

const getMappedLocalBellSound = (bell) => {
    if (bell?.type === "NATIONAL_ANTHEM") {
        return NATIONAL_ANTHEM_SOUND;
    }

    const soundCandidates = [bell?.url, bell?.uri, bell?.bell, bell?.music, bell?.fileUrl];

    for (const soundCandidate of soundCandidates) {
        const normalizedSound = normalizeSoundPath(soundCandidate);
        const matchedFile = BELL_SOUND_FILES.find((file) => {
            const normalizedFile = file.toLowerCase();
            return normalizedSound === normalizedFile || normalizedSound.endsWith(`/${normalizedFile}`);
        });

        if (matchedFile) {
            return `/bells/${matchedFile}`;
        }
    }

    return null;
};

// The local bundled sound to fall back to when the backend R2 file is null,
// empty or unreachable.
const getLocalFallbackSound = (bell) => {
    const mappedLocalSound = getMappedLocalBellSound(bell);

    if (mappedLocalSound) {
        return mappedLocalSound;
    }

    return bell?.type === "NATIONAL_ANTHEM" ? NATIONAL_ANTHEM_SOUND : DEFAULT_BELL_SOUND;
};

const getPlaybackSlot = (date, time, bellKey) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}-${time}-${bellKey}`;
};

const claimPlaybackSlot = (slot) => {
    const now = Date.now();
    const storedValue = localStorage.getItem(LAST_PLAYED_SLOT_STORAGE_KEY);
    let playedSlots = {};

    try {
        playedSlots = storedValue ? JSON.parse(storedValue) : {};
    } catch (e) {
        playedSlots = storedValue === slot ? {[slot]: now} : {};
    }

    if (playedSlots[slot]) {
        return false;
    }

    Object.keys(playedSlots).forEach((playedSlot) => {
        if (now - Number(playedSlots[playedSlot]) > PLAYED_SLOT_TTL_MS) {
            delete playedSlots[playedSlot];
        }
    });

    playedSlots[slot] = now;
    localStorage.setItem(LAST_PLAYED_SLOT_STORAGE_KEY, JSON.stringify(playedSlots));
    return true;
};

const getBellKey = (bell) => {
    return bell?.id ?? bell?.bellSetupId ?? bell?.name ?? bell?.hourTime ?? "bell";
};

const getBellScheduleDate = (baseDate, bellTime) => {
    const match = String(bellTime || "").trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);

    if (!match) {
        return null;
    }

    let hours = Number(match[1]);
    const minutes = Number(match[2]);
    const ampm = match[3].toUpperCase();

    if (Number.isNaN(hours) || Number.isNaN(minutes) || hours < 1 || hours > 12 || minutes > 59) {
        return null;
    }

    if (ampm === "PM" && hours !== 12) {
        hours += 12;
    }

    if (ampm === "AM" && hours === 12) {
        hours = 0;
    }

    const scheduleDate = new Date(baseDate);
    scheduleDate.setHours(hours, minutes, 0, 0);
    return scheduleDate;
};

const shouldPlayBellNow = (bell, now) => {
    if (bell.status !== "Y") {
        return null;
    }

    const scheduleDate = getBellScheduleDate(now, bell.hourTime);

    if (!scheduleDate) {
        return null;
    }

    const delayMs = now.getTime() - scheduleDate.getTime();
    return delayMs >= 0 && delayMs <= SCHEDULE_LOOKBACK_MS ? scheduleDate : null;
};

const setIsPlayingLS = (bell,val) => {
    const isPlayingStr = String(val);                // "true" | "false"

    const bellIdStr = val && bell?.id != null ? String(bell.id) : "";

    // persist to localStorage (storage event केवल अन्य tab मा fire हुन्छ)
    localStorage.setItem("isPlaying", isPlayingStr);
    localStorage.setItem("currentBellId", bellIdStr);

    // same-tab listeners लाई सूचित गर्ने (custom event)
    window.dispatchEvent(
        new CustomEvent(LS_EVENT, { detail: { key: "isPlaying", value: isPlayingStr } })
    );
    window.dispatchEvent(
        new CustomEvent(LS_EVENT, { detail: { key: "currentBellId", value: bellIdStr } })
    );    // notify same-tab listeners
};

const BellScheduler = () => {
    const [bellData, setBellData] = useState([]);
    const playedBells = useRef({});
    const bellDataRef = useRef([]);

    useEffect(() => {
        bellDataRef.current = bellData;
    }, [bellData]);

    useEffect(() => {
        const loadBellData = (event) => {
            const storedData = event?.detail?.bellInfo || readBellInfo();
            console.log("Loading Bell data from local storage:", storedData);
            setBellData(storedData);
        };

        const handleStorageChange = (event) => {
            if (event.key === BELL_INFO_STORAGE_KEY) {
                loadBellData();
            }
        };

        loadBellData();
        window.addEventListener(BELL_INFO_UPDATED_EVENT, loadBellData);
        window.addEventListener("storage", handleStorageChange);

        return () => {
            window.removeEventListener(BELL_INFO_UPDATED_EVENT, loadBellData);
            window.removeEventListener("storage", handleStorageChange);
        };
    }, []);

    useEffect(() => {
        console.log("Bell Scheduler worker started");

        const evaluateSchedule = (dateValue = Date.now()) => {
            const now = new Date(dateValue);

            bellDataRef.current.forEach((bell) => {
                const scheduleDate = shouldPlayBellNow(bell, now);

                if (!scheduleDate) {
                    return;
                }

                const playbackSlot = getPlaybackSlot(scheduleDate, bell.hourTime, getBellKey(bell));

                if (!playedBells.current[playbackSlot] && claimPlaybackSlot(playbackSlot)) {
                    playBell(bell);
                    // Fire-and-forget audit log of this play. Never awaited and
                    // never throws, so a failed/slow API can't affect playback.
                    logBellPlay(bell, {playedAt: scheduleDate.toISOString()});
                    playedBells.current[playbackSlot] = true;

                    setTimeout(() => {
                        delete playedBells.current[playbackSlot];
                    }, SCHEDULE_LOOKBACK_MS);
                }
            });
        };

        const publicUrl = process.env.PUBLIC_URL || "";
        const workerUrl = `${publicUrl.replace(/\/$/, "")}/bell-scheduler-worker.js`;
        let worker = null;
        let fallbackInterval = null;

        if (window.Worker) {
            worker = new Worker(workerUrl);
            worker.onmessage = (event) => {
                if (event.data?.type === "tick") {
                    evaluateSchedule(event.data.now);
                }
            };
            worker.onerror = () => {
                worker?.terminate();
                worker = null;
                fallbackInterval = setInterval(() => evaluateSchedule(), 1000);
            };
            worker.postMessage({type: "start"});
        } else {
            fallbackInterval = setInterval(() => evaluateSchedule(), 1000);
        }

        const handleVisibilityChange = () => evaluateSchedule();
        window.addEventListener("focus", handleVisibilityChange);
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            worker?.postMessage({type: "stop"});
            worker?.terminate();
            clearInterval(fallbackInterval);
            window.removeEventListener("focus", handleVisibilityChange);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, []);

    const playBell = async (bell) => {
        // Priority 1: the backend R2 file — preferring the locally cached blob
        // (works even offline) and falling back to streaming the live URL.
        const remoteUrl = getRemoteBellUrl(bell);
        const cachedObjectUrl = remoteUrl ? await getCachedBellObjectUrl(remoteUrl) : null;
        const primarySound = cachedObjectUrl || remoteUrl || getLocalFallbackSound(bell);

        console.log("Bell URL:", primarySound);
        let audio = new Audio(primarySound);
        // Always play bells at full loudness, regardless of any prior state.
        audio.volume = 1;
        audio.muted = false;
        let isFallback = false;
        let objectUrlToRevoke = cachedObjectUrl;

        const revokeObjectUrl = () => {
            if (objectUrlToRevoke) {
                URL.revokeObjectURL(objectUrlToRevoke);
                objectUrlToRevoke = null;
            }
        };

        const startPlayback = () => {
            audio.play()
                .then(() => {
                    setIsPlayingLS(bell,true); // <— notify + persist
                    console.log(`Playing: ${bell.name} at ${bell.hourTime}`);
                })
                .catch((err) => {
                    console.warn("Primary bell failed, playing fallback bell:", err);
                    playFallback();
                });
        };

        // Priority 2: the bundled local sound in /public/bells.
        const playFallback = () => {
            if (!isFallback) {
                isFallback = true;
                revokeObjectUrl();
                const fallbackSound = getLocalFallbackSound(bell);
                if (fallbackSound === primarySound) {
                    return;
                }
                audio = new Audio(fallbackSound);
                audio.volume = 1;
                audio.muted = false;
                audio.onended = () => setIsPlayingLS(bell,false);
                audio.play()
                    .then(() => setIsPlayingLS(bell,true))
                    .catch(err => {
                        console.error("Fallback bell also failed:", err);
                    });
            }
        };

        audio.onended = () => {
            setIsPlayingLS(bell,false); // <— notify + persist
            revokeObjectUrl();
            console.log("Bell playback completed.");
        };

        audio.addEventListener("error", () => {
            console.warn("Error loading primary bell, using fallback.");
            playFallback();
        });

        startPlayback(bell);

        // NATIONAL_ANTHEM and MUSIC bells always play to completion — the
        // playTime cutoff is ignored so they are never truncated mid-track.
        const playFull = bell?.type === "NATIONAL_ANTHEM" || bell?.type === "MUSIC";

        if (playFull) {
            return;
        }

        // Force stop after playTime seconds
        setTimeout(() => {
            try {
                audio.pause();
                audio.currentTime = 0;
            } catch {}
            revokeObjectUrl();
            setIsPlayingLS(bell,false); // <— notify + persist
        }, parseInt(bell.playTime, 10) * 1000);
    };

    return null;
};

export default BellScheduler;
